import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import {
  createTimelineDateChange,
  type TimelineDatePreview,
  type TimelineDateChange,
} from "./timeline-drag-utils";
import {
  type TimelineBarStyle,
  type TimelineItem,
  type TimelinePreviewLabels,
} from "./timeline-item.types";
import {
  getTimelineDateAtPosition,
  getTimelineDayWidth,
  getTimelineSpanMetrics,
  getTimelineStepWidth,
  getTimelineTickWidth,
  normalizeTimelineRange,
  type TimelineRange,
  type TimelineTimeScale,
  type TimelineZoom,
} from "./timeline-range";
import { startOfLocalDay } from "./timeline-date-utils";
import {
  TIMELINE_BAR_HEIGHT,
  TIMELINE_BAR_TOP,
  TimelineBar,
} from "./TimelineBar";

const TODAY_COLUMN_BACKGROUND = "var(--mela-color-primary-soft-bg)";
const GRID_LINE_BACKGROUND = "#e3e7ed";
const WEEKEND_COLUMN_BACKGROUND = "rgba(248, 250, 252, 0.72)";

interface CreateDragState {
  startDayIndex: number;
  endDayIndex: number;
}

interface TimelineCellProps {
  item: TimelineItem;
  range: TimelineRange;
  zoom: TimelineZoom;
  timeScale?: TimelineTimeScale;
  width: number;
  viewportStart?: number;
  viewportWidth?: number;
  todayPosition: number;
  barStyle: TimelineBarStyle;
  ariaLabel: string;
  previewLabels: TimelinePreviewLabels;
  formatPreviewDuration: (preview: TimelineDatePreview) => string;
  getScrollContainer: () => HTMLElement | null;
  pendingDateChange?: TimelineDateChange | null;
  onItemOpen?: (item: TimelineItem) => void;
  onDateChangeRequest: (change: TimelineDateChange) => void;
}

interface TimelineGridCellProps {
  range: TimelineRange;
  zoom: TimelineZoom;
  width: number;
  viewportStart?: number;
  viewportWidth?: number;
  todayPosition: number;
  backgroundColor?: string;
}

const formatPixel = (value: number): string =>
  `${Math.round(value * 1000) / 1000}px`;

const getGridColumnWidth = (zoom: TimelineZoom): number => {
  return zoom === "day"
    ? getTimelineDayWidth(zoom)
    : getTimelineTickWidth(zoom);
};

const getRepeatingDayColumnBackground = ({
  dayWidth,
  offset,
  color,
}: {
  dayWidth: number;
  offset: number;
  color: string;
}): string => {
  const cycleWidth = dayWidth * 7;
  const start = offset;
  const end = offset + dayWidth;

  if (end > cycleWidth) {
    const wrappedEnd = end - cycleWidth;

    return [
      "repeating-linear-gradient(to right",
      `${color} 0 ${formatPixel(wrappedEnd)}`,
      `transparent ${formatPixel(wrappedEnd)} ${formatPixel(start)}`,
      `${color} ${formatPixel(start)} ${formatPixel(cycleWidth)})`,
    ].join(", ");
  }

  return [
    "repeating-linear-gradient(to right",
    `transparent 0 ${formatPixel(start)}`,
    `${color} ${formatPixel(start)} ${formatPixel(end)}`,
    `transparent ${formatPixel(end)} ${formatPixel(cycleWidth)})`,
  ].join(", ");
};

const getLocalRepeatingOffset = (
  absoluteOffset: number,
  viewportStart: number,
  cycleWidth: number,
): number => {
  if (cycleWidth <= 0) {
    return absoluteOffset;
  }

  return (
    (((absoluteOffset - (viewportStart % cycleWidth)) % cycleWidth) +
      cycleWidth) %
    cycleWidth
  );
};

const getRepeatingGridLineBackground = ({
  columnWidth,
  offset,
  color,
}: {
  columnWidth: number;
  offset: number;
  color: string;
}): string => {
  const start = offset;
  const end = Math.min(columnWidth, start + 1);

  return [
    "repeating-linear-gradient(to right",
    `transparent 0 ${formatPixel(start)}`,
    `${color} ${formatPixel(start)} ${formatPixel(end)}`,
    `transparent ${formatPixel(end)} ${formatPixel(columnWidth)})`,
  ].join(", ");
};

const getTimelineCellBackgroundImage = ({
  range,
  zoom,
  width,
  todayPosition,
  viewportStart,
}: {
  range: TimelineRange;
  zoom: TimelineZoom;
  width: number;
  todayPosition: number;
  viewportStart: number;
}): string => {
  const gridWidth = getGridColumnWidth(zoom);
  const gridLineStart = getLocalRepeatingOffset(
    Math.max(0, gridWidth - 1),
    viewportStart,
    gridWidth,
  );
  const layers = [
    getRepeatingGridLineBackground({
      columnWidth: gridWidth,
      offset: gridLineStart,
      color: GRID_LINE_BACKGROUND,
    }),
  ];

  const dayWidth = getTimelineDayWidth(zoom);

  if (zoom !== "day") {
    return layers.join(", ");
  }

  const todayColumnStart =
    Math.floor(todayPosition / dayWidth) * dayWidth - viewportStart;
  const todayEnd = Math.min(width, todayColumnStart + dayWidth);

  if (todayColumnStart < width && todayEnd > 0) {
    const visibleTodayStart = Math.max(0, todayColumnStart);
    const visibleTodayEnd = Math.min(width, todayEnd);

    layers.push(
      [
        "linear-gradient(to right",
        `transparent 0 ${formatPixel(visibleTodayStart)}`,
        `${TODAY_COLUMN_BACKGROUND} ${formatPixel(
          visibleTodayStart,
        )} ${formatPixel(visibleTodayEnd)}`,
        `transparent ${formatPixel(visibleTodayEnd)} 100%)`,
      ].join(", "),
    );
  }

  const rangeStart = startOfLocalDay(normalizeTimelineRange(range).start);
  const rangeStartWeekday = rangeStart.getDay();
  const cycleWidth = dayWidth * 7;
  const saturdayOffset = getLocalRepeatingOffset(
    ((6 - rangeStartWeekday + 7) % 7) * dayWidth,
    viewportStart,
    cycleWidth,
  );
  const sundayOffset = getLocalRepeatingOffset(
    ((0 - rangeStartWeekday + 7) % 7) * dayWidth,
    viewportStart,
    cycleWidth,
  );

  layers.push(
    getRepeatingDayColumnBackground({
      dayWidth,
      offset: saturdayOffset,
      color: WEEKEND_COLUMN_BACKGROUND,
    }),
    getRepeatingDayColumnBackground({
      dayWidth,
      offset: sundayOffset,
      color: WEEKEND_COLUMN_BACKGROUND,
    }),
  );

  return layers.join(", ");
};

export const TimelineGridCell = React.memo(function TimelineGridCell({
  range,
  zoom,
  width,
  viewportStart = 0,
  viewportWidth = width,
  todayPosition,
  backgroundColor = "#ffffff",
}: TimelineGridCellProps) {
  const safeViewportStart = Math.max(0, Math.min(width, viewportStart));
  const safeViewportWidth = Math.max(
    0,
    Math.min(width - safeViewportStart, viewportWidth),
  );
  const backgroundImage = useMemo(
    () =>
      getTimelineCellBackgroundImage({
        range,
        zoom,
        width: safeViewportWidth,
        todayPosition,
        viewportStart: safeViewportStart,
      }),
    [range, safeViewportStart, safeViewportWidth, todayPosition, zoom],
  );

  if (safeViewportWidth <= 0) {
    return null;
  }

  return (
    <Box
      aria-hidden
      data-timeline-grid-cell
      sx={{
        position: "absolute",
        left: safeViewportStart,
        width: safeViewportWidth,
        minWidth: safeViewportWidth,
        height: "100%",
        backgroundColor,
        backgroundImage,
      }}
    />
  );
});

export const TimelineCell = React.memo(function TimelineCell({
  item,
  range,
  zoom,
  timeScale,
  width,
  viewportStart = 0,
  viewportWidth = width,
  todayPosition,
  barStyle,
  ariaLabel,
  previewLabels,
  formatPreviewDuration,
  getScrollContainer,
  pendingDateChange,
  onItemOpen,
  onDateChangeRequest,
}: TimelineCellProps) {
  const metrics = getTimelineSpanMetrics(item, range, zoom, timeScale);
  const stepWidth = getTimelineStepWidth(zoom);
  const safeViewportStart = Math.max(0, Math.min(width, viewportStart));
  const safeViewportWidth = Math.max(
    0,
    Math.min(width - safeViewportStart, viewportWidth),
  );
  const safeViewportEnd = safeViewportStart + safeViewportWidth;
  const barClipStartOffset = metrics
    ? Math.max(0, safeViewportStart - metrics.left)
    : 0;
  const barClipEndOffset = metrics
    ? Math.max(0, metrics.left + metrics.width - safeViewportEnd)
    : 0;
  const isBarVisible = Boolean(
    metrics &&
      metrics.left + metrics.width >= safeViewportStart &&
      metrics.left <= safeViewportEnd,
  );
  const [createDragState, setCreateDragState] =
    useState<CreateDragState | null>(null);
  const createDragStateRef = useRef<CreateDragState | null>(null);
  const createDragRectRef = useRef<DOMRect | null>(null);
  const canCreateRange = !metrics;

  const getDayIndexFromClientX = useCallback(
    (clientX: number, rect: DOMRect) => {
      const position = Math.min(
        width - 1,
        Math.max(0, safeViewportStart + clientX - rect.left),
      );

      return Math.floor(position / stepWidth);
    },
    [safeViewportStart, stepWidth, width],
  );

  const handleCreatePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!canCreateRange) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const rect = event.currentTarget.getBoundingClientRect();
      const startDayIndex = getDayIndexFromClientX(event.clientX, rect);
      const nextState = {
        startDayIndex,
        endDayIndex: startDayIndex,
      };

      createDragRectRef.current = rect;
      createDragStateRef.current = nextState;
      setCreateDragState(nextState);
    },
    [canCreateRange, getDayIndexFromClientX],
  );

  useEffect(() => {
    if (!createDragState) {
      return;
    }

    const handleMove = (event: PointerEvent) => {
      const currentDragState = createDragStateRef.current;

      if (!currentDragState) {
        return;
      }

      const rect = createDragRectRef.current;

      if (!rect) {
        return;
      }

      const position = Math.min(
        width - 1,
        Math.max(0, safeViewportStart + event.clientX - rect.left),
      );
      const nextState = {
        ...currentDragState,
        endDayIndex: Math.floor(position / stepWidth),
      };

      createDragStateRef.current = nextState;
      setCreateDragState(nextState);
    };

    const handleUp = () => {
      const currentDragState = createDragStateRef.current;
      createDragRectRef.current = null;
      createDragStateRef.current = null;
      setCreateDragState(null);

      if (!currentDragState) {
        return;
      }

      const startDayIndex = Math.min(
        currentDragState.startDayIndex,
        currentDragState.endDayIndex,
      );
      const endDayIndex = Math.max(
        currentDragState.startDayIndex,
        currentDragState.endDayIndex,
      );
      const nextStartDate = getTimelineDateAtPosition(
        startDayIndex * stepWidth,
        range,
        zoom,
        timeScale,
      );
      const nextDueDate = getTimelineDateAtPosition(
        endDayIndex * stepWidth,
        range,
        zoom,
        timeScale,
      );

      onDateChangeRequest(
        createTimelineDateChange(item, nextStartDate, nextDueDate),
      );
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp, { once: true });

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [
    createDragState,
    item,
    onDateChangeRequest,
    range,
    safeViewportStart,
    stepWidth,
    timeScale,
    width,
    zoom,
  ]);

  const createPreview = useMemo(() => {
    if (!createDragState) {
      return null;
    }

    const startDayIndex = Math.min(
      createDragState.startDayIndex,
      createDragState.endDayIndex,
    );
    const endDayIndex = Math.max(
      createDragState.startDayIndex,
      createDragState.endDayIndex,
    );

    return {
      left: startDayIndex * stepWidth - safeViewportStart,
      width: Math.max(stepWidth, (endDayIndex - startDayIndex + 1) * stepWidth),
    };
  }, [createDragState, safeViewportStart, stepWidth]);

  const backgroundImage = useMemo(
    () =>
      getTimelineCellBackgroundImage({
        range,
        zoom,
        width: safeViewportWidth,
        todayPosition,
        viewportStart: safeViewportStart,
      }),
    [range, safeViewportStart, safeViewportWidth, todayPosition, zoom],
  );

  if (safeViewportWidth <= 0) {
    return null;
  }

  return (
    <Box
      data-timeline-cell-id={item.id}
      onPointerDown={handleCreatePointerDown}
      sx={{
        position: "absolute",
        left: safeViewportStart,
        width: safeViewportWidth,
        minWidth: safeViewportWidth,
        height: "100%",
        minHeight: 46,
        backgroundColor: "#ffffff",
        backgroundImage,
        cursor: canCreateRange ? "crosshair" : "default",
        overflow: "hidden",
      }}
    >
      {metrics && isBarVisible ? (
        <TimelineBar
          item={item}
          left={metrics.left - safeViewportStart}
          width={metrics.width}
          range={range}
          zoom={zoom}
          timeScale={timeScale}
          continuesBefore={metrics.continuesBefore || barClipStartOffset > 0}
          continuesAfter={metrics.continuesAfter || barClipEndOffset > 0}
          clipStartOffset={barClipStartOffset}
          clipEndOffset={barClipEndOffset}
          barStyle={barStyle}
          ariaLabel={ariaLabel}
          previewLabels={previewLabels}
          formatPreviewDuration={formatPreviewDuration}
          getScrollContainer={getScrollContainer}
          pendingDateChange={pendingDateChange}
          onItemOpen={onItemOpen}
          onDateChangeRequest={onDateChangeRequest}
        />
      ) : null}

      {createPreview && (
        <Box
          sx={{
            position: "absolute",
            top: TIMELINE_BAR_TOP,
            left: createPreview.left,
            width: createPreview.width,
            height: TIMELINE_BAR_HEIGHT,
            borderRadius: 1,
            border: "1px dashed #149ee7",
            backgroundColor: "rgba(20, 158, 231, 0.12)",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.12)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />
      )}
    </Box>
  );
});
